import { useState } from "react";
import { Check, X, AlertTriangle, ClockIcon, MailIcon } from "lucide-react";
import { ReceivedInvite } from "@/types/mateTypes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAcceptInvite, useRejectInvite } from "@/hooks/useMates";
import { useToastHandler } from "@/hooks/useToastHandler";
import { formatRelativeTimeWithPrefix } from "@/lib/dateUtils";
import {
  Card,
  CardTitle,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MATE_CONFIG } from "@/config/mateConfig";

interface ReceivedInvitesProps {
  invites: ReceivedInvite[];
}

export function ReceivedInvites({ invites }: ReceivedInvitesProps) {
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<ReceivedInvite | null>(
    null
  );
  const [actionType, setActionType] = useState<"accept" | "reject" | null>(
    null
  );
  const acceptMutation = useAcceptInvite();
  const rejectMutation = useRejectInvite();
  const { handleSuccess } = useToastHandler();

  if (invites.length === 0) {
    return null;
  }

  const handleActionClick = (
    invite: ReceivedInvite,
    action: "accept" | "reject"
  ) => {
    setSelectedInvite(invite);
    setActionType(action);
    setShowActionDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedInvite || !actionType) return;

    try {
      if (actionType === "accept") {
        await acceptMutation.mutateAsync(selectedInvite.token);
        handleSuccess(
          `You're now workout mates with ${selectedInvite.inviter_user.display_name}!`,
          3000
        );
      } else {
        await rejectMutation.mutateAsync(selectedInvite.token);
        handleSuccess("Invitation declined.", 3000);
      }
      setShowActionDialog(false);
      setSelectedInvite(null);
      setActionType(null);
    } catch {
      // Error handled by mutation
      setShowActionDialog(false);
      setSelectedInvite(null);
      setActionType(null);
    }
  };

  const isLoading = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <>
      <Card className="w-full mb-8">
        <CardHeader>
          <CardTitle>Received Invitations</CardTitle>
          <CardDescription>{MATE_CONFIG.MATE_DESCRIPTION}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="min-w-0 p-4 border border-gray-200 bg-bg-base rounded-lg"
              >
                <div className="flex items-start gap-3 mb-4">
                  <Avatar className="h-16 w-16 border-2 border-gray-200">
                    <AvatarImage
                      src={invite.inviter_user.avatar_url || undefined}
                      alt={`${invite.inviter_user.display_name} avatar`}
                      loading="lazy"
                    />
                    <AvatarFallback className="text-sm font-medium bg-gray-100 text-gray-600">
                      N/A
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="text-sm/6 font-semibold text-gray-900 mb-1">
                      {invite.inviter_user.display_name}
                    </p>

                    <div className="flex items-center gap-x-2 text-gray-500 mb-1">
                      <MailIcon className="h-3 w-3" />
                      <span className="text-xs">
                        {invite.inviter_user.email}
                      </span>
                    </div>

                    <div className="flex items-center gap-x-2 text-gray-500">
                      <ClockIcon className="h-3 w-3" />
                      <span className="text-xs">
                        {formatRelativeTimeWithPrefix(
                          invite.created_at,
                          "Invited",
                          "Recently invited"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {invite.status === "rejected" ? (
                  <p className="text-tx-error text-xs mt-2">
                    {formatRelativeTimeWithPrefix(
                      invite.rejected_at,
                      "You have rejected",
                      "Just rejected"
                    )}
                  </p>
                ) : (
                  <div className="mt-3 flex items-center gap-x-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleActionClick(invite, "accept")}
                      disabled={isLoading}
                      className="w-1/2"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleActionClick(invite, "reject")}
                      disabled={isLoading}
                      className="w-1/2"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="p-6 border-0 overflow-hidden max-w-md bg-white">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Confirm
          </DialogTitle>
          <div className="border-t border-gray-200"></div>
          <DialogDescription className=" text-tx-default text-base mb-5">
            {actionType === "accept"
              ? `After accepting, you'll be "mates" and be able to see each other's recent workouts. You can cancel this relationship anytime.`
              : `Are you sure you don't want to be workout mates with ${selectedInvite?.inviter_user.display_name}?`}
          </DialogDescription>

          <div className="flex">
            <Button
              variant={actionType === "accept" ? "primary" : "destructive"}
              size="lg"
              onClick={handleConfirmAction}
              disabled={isLoading}
              className="flex-1"
            >
              {actionType === "accept" ? (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  {isLoading ? "Accepting..." : "Accept Invitation"}
                </>
              ) : (
                <>{isLoading ? "Declining..." : "Decline Invitation"}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
